import { useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

type DragHandler = (dragIndex: number, hoverIndex: number) => void;

interface DraggableItemParams {
  dragItem?: DragHandler;
  dropItem?: DragHandler;
  /** Called when a drag operation begins (item is picked up). */
  onDragBegin?: () => void;
  /** Called when a drag operation ends. `didDrop` is true if a compatible target handled the drop. */
  onDragEnd?: (didDrop: boolean) => void;
  /**
   * Bounds used for the midpoint crossing check. Defaults to the drop node's full rect.
   * Callers with tall composed rows can return a smaller header/row rect so nested content
   * does not inflate the threshold.
   */
  getHoverBoundingRect?: (dropNode: HTMLElement) => DOMRect;
  /**
   * When true, suppress the browser's floating drag ghost. Use when the list already
   * shows an in-place dragging affordance (e.g. a highlighted source row).
   */
  hideDragPreview?: boolean;
  /**
   * When false, skip subscribing to isDragging/isOver monitor updates. Callers that track
   * drag identity themselves (e.g. OrderableList) avoid extra re-renders on hover.
   */
  collectMonitorState?: boolean;
  /**
   * Live index getter for callers that preview reorder without re-rendering (e.g. CSS
   * `order`). Falls back to `index` when omitted.
   */
  getIndex?: () => number;
  dragItemType?: string;
  index?: number;
  disabled?: boolean;
}

/**
 * Whether a hover should trigger a reorder, based on vertical midpoint crossing.
 *
 * Without this, tall rows thrash: a swap moves the target out from under the cursor,
 * which immediately swaps back.
 */
export function shouldReorderOnHoverMidpoint(params: {
  dragIndex: number;
  targetIndex: number;
  /** Pointer Y relative to the top of the hover bounding rect. */
  hoverClientY: number;
  /** Half the hover bounding rect height. */
  hoverMiddleY: number;
}): boolean {
  const { dragIndex, targetIndex, hoverClientY, hoverMiddleY } = params;

  if (dragIndex === targetIndex) {
    return false;
  }

  // Dragging downward: wait until cursor is below the midpoint.
  if (dragIndex < targetIndex && hoverClientY < hoverMiddleY) {
    return false;
  }
  // Dragging upward: wait until cursor is above the midpoint.
  if (dragIndex > targetIndex && hoverClientY > hoverMiddleY) {
    return false;
  }

  return true;
}

const AUTO_SCROLL_EDGE_PX = 60;
const AUTO_SCROLL_MAX_SPEED_PX = 14;

/**
 * Scroll delta for one animation frame, given the pointer's Y position and the
 * scrollable container's bounding rect. Positive scrolls down, negative scrolls up,
 * zero outside the edge threshold. Only the edge nearer to the pointer is ever
 * considered (and its distance is clamped to zero rather than requiring the pointer
 * stay inside the rect) so a container shorter than 2*AUTO_SCROLL_EDGE_PX can't have
 * its top-edge check win when the pointer is actually closer to the bottom, and a
 * pointer sample that lands just outside the rect still scrolls at full speed.
 */
export function computeAutoScrollDelta(clientY: number, containerRect: DOMRect): number {
  const distanceFromTop = clientY - containerRect.top;
  const distanceFromBottom = containerRect.bottom - clientY;
  const nearerEdgeIsTop = distanceFromTop <= distanceFromBottom;
  const distance = Math.max(nearerEdgeIsTop ? distanceFromTop : distanceFromBottom, 0);

  if (distance >= AUTO_SCROLL_EDGE_PX) {
    return 0;
  }
  const intensity = 1 - distance / AUTO_SCROLL_EDGE_PX;
  return nearerEdgeIsTop ? -AUTO_SCROLL_MAX_SPEED_PX * intensity : AUTO_SCROLL_MAX_SPEED_PX * intensity;
}

export function findScrollableAncestor(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Scrolls `container` while the pointer stays near its top/bottom edge during an
 * HTML5 drag. `dragover` is the only drag event with live coordinates, but browsers
 * throttle it while the pointer is stationary, so a rAF loop re-applies the last
 * computed delta every frame for smooth, continuous scrolling. The container's rect
 * is read once up front rather than per `dragover` (scrolling `container` doesn't
 * move it) and refreshed on `resize` to cover a window resize/orientation change
 * mid-drag; a layout shift that doesn't fire `resize` (e.g. a sibling panel
 * collapsing without changing the viewport) is a known, accepted gap.
 */
export function startAutoScroll(container: HTMLElement): () => void {
  let containerRect = container.getBoundingClientRect();
  let delta = 0;
  let rafId: number;

  function handleDragOver(event: DragEvent) {
    delta = computeAutoScrollDelta(event.clientY, containerRect);
  }

  function handleResize() {
    containerRect = container.getBoundingClientRect();
  }

  function tick() {
    if (delta !== 0) {
      container.scrollTop += delta;
    }
    rafId = requestAnimationFrame(tick);
  }

  // passive: handleDragOver never calls preventDefault, so the browser can skip
  // waiting for it before proceeding with the drag.
  document.addEventListener("dragover", handleDragOver, { passive: true });
  window.addEventListener("resize", handleResize);
  rafId = requestAnimationFrame(tick);

  return () => {
    document.removeEventListener("dragover", handleDragOver);
    window.removeEventListener("resize", handleResize);
    cancelAnimationFrame(rafId);
  };
}

export default function useDraggableItem<T extends { index: number }>({
  dragItem,
  dropItem,
  onDragBegin,
  onDragEnd,
  getHoverBoundingRect,
  hideDragPreview = false,
  collectMonitorState = true,
  getIndex,
  dragItemType = "draggable-item",
  index = 0,
  disabled = false,
}: DraggableItemParams) {
  const dragRef = useRef(null);
  const dropRef = useRef(null);
  const autoScrollCleanupRef = useRef<(() => void) | null>(null);

  // Only the live index needs a ref: OrderableList updates it during CSS-order previews
  // without React re-renders. Handlers use normal closures and stay in sync on render.
  const getIndexRef = useRef(getIndex);
  getIndexRef.current = getIndex;

  const resolveIndex = (): number => getIndexRef.current?.() ?? index;

  const [{ isOver }, drop] = useDrop({
    accept: dragItemType,
    canDrop: () => !disabled,
    hover(item: T, monitor) {
      const dropNode = dropRef.current as HTMLElement | null;
      if (!dropNode || disabled) {
        return;
      }

      const targetIndex = resolveIndex();
      const dragIndex = item.index;

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }

      const hoverBoundingRect = (getHoverBoundingRect ?? defaultHoverBoundingRect)(dropNode);
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (
        !shouldReorderOnHoverMidpoint({
          dragIndex,
          targetIndex,
          hoverClientY,
          hoverMiddleY,
        })
      ) {
        return;
      }

      dragItem?.(dragIndex, targetIndex);
      // Mutate in place (standard react-dnd hover-reorder): subsequent hovers must see the
      // item's current index, or they'd keep firing reorders from the original dragIndex.
      item.index = targetIndex;
    },
    drop(item: T) {
      if (!dropRef.current || disabled) {
        return;
      }

      dropItem?.(item.index, resolveIndex());
    },
    collect: collectMonitorState
      ? (monitor) => ({
          isOver: monitor.isOver(),
        })
      : () => ({ isOver: false }),
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: dragItemType,
    item: () => {
      // dropRef, not dragRef: it's the whole row in every caller, so its ancestor
      // chain reflects the row's position regardless of how deep the drag handle sits.
      const scrollContainer = findScrollableAncestor(dropRef.current as HTMLElement | null);
      if (scrollContainer) {
        autoScrollCleanupRef.current = startAutoScroll(scrollContainer);
      }
      onDragBegin?.();
      return { index: resolveIndex() };
    },
    canDrag: () => !disabled,
    end: (_item, monitor) => {
      autoScrollCleanupRef.current?.();
      autoScrollCleanupRef.current = null;
      onDragEnd?.(monitor.didDrop());
    },
    collect: collectMonitorState
      ? (monitor) => ({
          isDragging: monitor.isDragging(),
        })
      : () => ({ isDragging: false }),
  });

  drag(dragRef);
  if (hideDragPreview) {
    drop(dropRef);
  } else {
    // Default: the drop node doubles as the HTML5 drag preview source.
    preview(drop(dropRef));
  }

  useEffect(() => {
    if (!hideDragPreview) {
      return;
    }
    // Empty image removes the cursor-following ghost when callers already show an
    // in-list dragging state (e.g. OrderableList's highlighted source row).
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [hideDragPreview, preview]);

  useEffect(() => {
    // Guards against a drag left in progress if the row unmounts mid-drag (e.g. the
    // list re-renders after a reorder).
    return () => autoScrollCleanupRef.current?.();
  }, []);

  return { dragRef, dropRef, isDragging, isOver };
}

function defaultHoverBoundingRect(dropNode: HTMLElement): DOMRect {
  return dropNode.getBoundingClientRect();
}
